import React from 'react';
// Imports only from core @react-pdf/renderer
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// --- Configuration Constants (Copied from MUI component) ---
const SALESLIST_CASH_COLUMNS = [
    { header: 'DEPT NO', key: 'DEPTNO', numeric: false },
    { header: 'INV NO', key: 'INVNO', numeric: false },
    { header: 'INV DATE', key: 'INV_DATE', numeric: false },
    { header: 'CUSTOMER', key: 'CUSTOMER', numeric: false },
    { header: 'SO NO', key: 'SO_NO', numeric: false },
    { header: 'LPO', key: 'LPO', numeric: false },
    { header: 'SALES MAN', key: 'SALES MAN', numeric: false, isLabel: true },
    { header: 'AMOUNT', key: 'AMOUNT', numeric: true },
    { header: 'CASHCRED', key: 'CASHCRED', numeric: false },
    { header: 'ACCOUNT', key: 'ACCOUNT', numeric: false },
    { header: 'VAT AMT', key: 'VATAMT', numeric: true },
    { header: 'COMMISSION', key: 'COMMISSION', numeric: true },
    { header: 'AMOUNT EXCLVAT', key: 'AMOUNT_EXCLVAT', numeric: true },
    { header: 'TYPE', key: 'TYPE', numeric: false },
];
const TOTAL_COLUMNS = SALESLIST_CASH_COLUMNS.length; // 14

// --- Helper Functions ---
const formatNumber = (val) => 
    Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatHeaderKey = (key) => key.toUpperCase(); 

// --- Column Widths Calculation ---
const NUMERIC_WIDTH = 6.5; 
const DESCRIPTIVE_WIDTH = 7.5; 

const getColumnWidth = (columnDef) => {
    return columnDef.numeric ? NUMERIC_WIDTH : DESCRIPTIVE_WIDTH;
}

// --- Stylesheet for PDF Component (No changes needed here as styles look safe) ---
const styles = StyleSheet.create({
    page: { flexDirection: 'column', backgroundColor: '#ffffff', padding: 20, fontFamily: 'Helvetica', fontSize: 8 },
    title: { fontSize: 14, marginBottom: 10, textAlign: 'center', fontWeight: 'bold' },
    table: { display: 'flex', width: '100%', borderStyle: 'solid', borderWidth: 1, borderRightWidth: 0, borderBottomWidth: 0 },
    tableRow: { margin: 'auto', flexDirection: 'row', height: 16, borderBottomColor: '#ccc', borderBottomWidth: 0.5 },
    tableHeader: { backgroundColor: '#e9e9e9', borderBottomWidth: 1.5, borderBottomColor: '#000', fontWeight: 'bold', height: 22 },
    columnHeader: { borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 3, textAlign: 'center', fontWeight: 'bold' },
    detailCell: { borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, padding: 2, overflow: 'hidden' },
    numericCell: { textAlign: 'right' },
    grandTotalRow: { backgroundColor: '#b3b3b3', borderTopWidth: 2, borderTopColor: '#000', height: 22, fontWeight: 'bold', flexDirection: 'row' },
    grandTotalCell: { padding: 4, borderStyle: 'solid', borderWidth: 1, borderLeftWidth: 0, borderTopWidth: 0, fontSize: 9, fontWeight: 'bold' },
    emptyData: { padding: 10, textAlign: 'center', color: 'gray' }
});


// --- The PDF Component ---

export const SalesTableFlatCASHPDF = ({ rows, grandColumnTotals, filters }) => {
    
    // Safety check
    if (!rows || rows.length === 0) {
        return (
            <Document title="Flat Sales (Cash/Credit) Report">
                <Page size="A4" style={styles.page} orientation="landscape">
                    <Text style={styles.emptyData}>No sales data found for the selected filters.</Text>
                </Page>
            </Document>
        );
    }
    
    // --- Renderer Functions ---

    const renderHeader = () => (
        <View style={[styles.tableRow, styles.tableHeader]} fixed>
            {SALESLIST_CASH_COLUMNS.map(col => (
                <Text 
                    key={col.key} 
                    style={[
                        styles.columnHeader, 
                        { width: `${getColumnWidth(col)}%` },
                        col.numeric ? styles.numericCell : {}
                    ]}
                >
                    {formatHeaderKey(col.header)}
                </Text>
            ))}
        </View>
    );

    const renderRow = (row, index) => (
        <View key={`row-cash-${index}`} style={styles.tableRow} wrap={false}>
            {SALESLIST_CASH_COLUMNS.map(col => {
                // Mimic the cell value retrieval logic from the MUI component
                const cellValue = row[col.key] || row[col.key.replace(/_/g, '')] || row[col.key.replace(/([A-Z])/g, ' $1').trim()];
                const content = col.numeric ? formatNumber(cellValue) : (col.key === 'INV_DATE' ? (cellValue?.split('T')[0] || '') : (cellValue || ''));
                const isAmountColumn = col.key === 'AMOUNT';

                return (
                    <Text 
                        key={col.key} 
                        style={[
                            styles.detailCell, 
                            { width: `${getColumnWidth(col)}%` }, 
                            col.numeric ? styles.numericCell : {},
                            isAmountColumn ? { fontWeight: 'bold' } : {}
                        ]}
                    >
                        {content}
                    </Text>
                );
            })}
        </View>
    );

    const renderFooter = () => {
        if (!grandColumnTotals || Object.keys(grandColumnTotals).length === 0) return null;

        return (
            <View style={[styles.grandTotalRow]} fixed>
                {SALESLIST_CASH_COLUMNS.map(col => {
                    const isNumericTotal = col.numeric;
                    const isLabelCell = col.isLabel;
                    const width = getColumnWidth(col);

                    let content = '';
                    let dynamicStyle = {};

                    if (isLabelCell) {
                        content = 'GRAND TOTAL:';
                        dynamicStyle = { textAlign: 'right' };
                    } else if (isNumericTotal) {
                        content = formatNumber(grandColumnTotals[col.key] || 0);
                        dynamicStyle = { textAlign: 'right' };
                    }
                    
                    return (
                        <Text 
                            key={`footer-${col.key}`} 
                            style={[
                                styles.grandTotalCell, 
                                { width: `${width}%` },
                                dynamicStyle
                            ]}
                        >
                            {content}
                        </Text>
                    );
                })}
            </View>
        );
    }


    return (
        <Document title="Flat Sales (Cash/Credit) Report">
            <Page size="A4" orientation="landscape" style={styles.page} wrap>
                <Text style={styles.title}>Flat Sales Analysis Report (14-Column Fixed)</Text>
                
                {/* Optional: Include Filter Summary */}
                {filters && (
                    <View style={{ marginBottom: 10 }}>
                        <Text style={{ fontSize: 7 }}>
                            Filters: Date Range ({filters?.startDate || 'N/A'} to {filters?.endDate || 'N/A'})
                        </Text>
                    </View>
                )}

                <View style={styles.table}>
                    {renderHeader()}
                    <View>
                        {rows.map(renderRow)}
                    </View>
                </View>

                {/* Footer is rendered outside the main table View for better fixed positioning */}
                {renderFooter()}

                {/* Page Numbering */}
                <Text style={{ position: 'absolute', bottom: 10, right: 20, fontSize: 7 }} fixed>
                    Page <Text render={({ pageNumber, totalPages }) => (`${pageNumber} of ${totalPages}`)} />
                </Text>
            </Page>
        </Document>
    );
};

export default SalesTableFlatCASHPDF;